/* Shared ECharts option fragments for the simulation charts.
 *
 * Six charts were each carrying their own copy of the same option scaffolding.
 * `formatDate` was byte-identical in all six, `getXAxisTitle` in all six, the
 * dataZoom slider+inside pair in five, the dark tooltip skin in five.
 *
 * ── What is shared here, and what deliberately is not ─────────────────────
 * Only the fragments that were genuinely identical, or that differ by one
 * clearly-named value (an accent colour, a font size), are shared. The copying
 * had drifted in several other places, and NOT every drift is a mistake:
 *
 *   grid margins       5 distinct values. NOT shared, NOT normalised. Each
 *                      chart's `top` tracks whether it renders a legend at
 *                      top:0, and its `bottom` tracks where that chart parks
 *                      its dataZoom slider (bottom:40 -> grid bottom 80;
 *                      Pricing's 5px slider at bottom:10 -> grid bottom 40).
 *                      They are coupled to each chart's own layout, and
 *                      `containLabel: true` already makes left/right adapt to
 *                      the tick labels. A single value would move the plot area
 *                      in five charts to fix nothing.
 *
 *   y-axis nameGap     5 values for the same "Energy (kWh)" label:
 *                      Consumption 40, Storage 40, Transfer 50, Overlay 55,
 *                      Production 60. NOT shared, NOT normalised. nameGap is a
 *                      pixel offset that has to clear that chart's widest tick
 *                      label, and these charts plot different magnitudes —
 *                      Production and Overlay carry summed totals, Consumption
 *                      and Storage single-device values. Unifying would either
 *                      collide the axis name with the labels on the wide charts
 *                      or open a gutter on the narrow ones.
 *
 *   splitLine          Production dashed #eee · Storage and Overlay dashed #ddd
 *                      · Pricing, Transfer and TransferDailyRange solid #eee ·
 *                      Consumption ABSENT, so it alone renders the ECharts
 *                      default (solid #E0E6F1, noticeably bluer than any
 *                      sibling). Left as found. Consumption's is the one value
 *                      that plainly was not chosen, but changing it is a
 *                      visible restyle, not a refactor — flagged for the
 *                      product owner rather than decided here.
 *
 *   tooltip skin       Three variants, all preserved, two of them expressible
 *                      through darkTooltip() below:
 *                        Consumption/Pricing/Transfer/TransferDailyRange
 *                          dark, 12px text, white 1px border
 *                        Production/Storage
 *                          dark, 14px text, default border
 *                        Overlay
 *                          no skin at all -> the ECharts default LIGHT tooltip
 *                          (white background, dark text) while the other five
 *                          are dark. Also flagged rather than decided: Overlay
 *                          is the dense multi-series summary chart and the
 *                          light box may well be intentional there.
 *
 * Anything listed as "left as found" renders exactly as it did before this
 * module existed. */

/**
 * `series.step` for a line whose points are PIECEWISE-CONSTANT BUCKETS.
 *
 * -- The test for whether a series belongs here ---------------------------
 * Ask one question, about the physical quantity rather than about the column:
 *
 *   IS THE UNDERLYING QUANTITY ACTUALLY CONSTANT ACROSS THE HOUR, or is the
 *   hourly figure a SAMPLE OR AVERAGE of something that varies continuously
 *   through it?
 *
 * Constant across the hour -> step. The value owns a whole interval and a line
 * that slopes between two samples draws a transition the quantity never made.
 *
 * An average of something continuous -> do NOT step. Stepping would assert the
 * quantity sat still for an hour and then jumped instantaneously on the
 * boundary, which is a misrepresentation in the opposite direction, and a worse
 * one: the real curve is smooth and the step invents 24 discontinuities a day
 * that the plant never experienced.
 *
 * Which side a series falls on, on this data:
 *
 *   DISPATCHED - battery charge/discharge, EV charge/discharge, generator
 *                output. The optimiser commands a setpoint and the device holds
 *                it for the interval. Genuinely piecewise constant, by
 *                construction. STEPS.
 *
 *   CONTINUOUS - solar production, building load. Irradiance and occupancy vary
 *                smoothly through the hour and the column is their mean.
 *                DOES NOT STEP.
 *
 *   TARIFF     - Buy/Sell $/kWh. Piecewise constant by definition: a TOU rate
 *                is held flat across a rate period and changes ON the hour.
 *                STEPS. This is the case that shows the test is not about
 *                energy at all - a rate is intensive, sums with nothing and
 *                shares an axis with nothing, and it still steps, because the
 *                question is constancy and not additivity.
 *
 *   MIXED      - net grid transfer, and the daily min/max/mean aggregates of
 *                it. The residual of the continuous terms and the dispatched
 *                ones, so it inherits the continuity: it cannot be flat across
 *                an hour in which the load it balances is not. DOES NOT STEP.
 *
 * Also excluded, for the same reason as the continuous group: a quantity read
 * at an instant rather than held across an interval. State of Charge is the
 * example in this data - it ramps between readings. No chart plots one today.
 *
 * -- What a result row timestamp means ------------------------------------
 * The sim builds its timelist with pd.date_range(start, end, freq="h") and
 * writes one row per stamp, so a year runs 01/01 00:00 .. 12/31 23:00 - 8784
 * rows for a leap year, first stamp 00:00 and last 23:00, never 24:00 and never
 * a row belonging to the following January. A stamp therefore names the START
 * of its bucket, and the row energy columns are the quantity accumulated over
 * [t, t+1).
 *
 * The result file states this a second time, in its own arithmetic. State of
 * Charge is an instantaneous reading at the stamp, energy is a bucket after it,
 * and the two are tied together one row apart. From 11 Top Picks Day A,
 * 01/02/2040 (m2040-2040-01-01_2040-12-31-timeseries-082726_2331.csv):
 *
 *     hour  EV Charge (kWh)   EV State of Charge (kWh)
 *     00    520.309           2200.000
 *     01    529.999           2694.294      = 2200.000 + 520.309 * 0.95
 *     02    291.789           3197.793      = 2694.294 + 529.999 * 0.95
 *
 * The charge stamped 00:00 lands on the SoC stamped 01:00. It is the energy of
 * the hour that BEGINS at 00:00, not the one that ends there.
 *
 * -- The rate columns use the same convention, checked independently ------
 * Not carried over from the energy columns by assumption: the rate arrives from
 * the tariff inputs, which need not share the timeseries labelling, and a rate
 * shifted by one hour would move every price boundary and misstate when
 * arbitrage pays. It was measured instead, on the husky-2040 result
 * (HuskyTerminal-2040-01-01_2040-12-31-timeseries-082726_1020.csv), which
 * carries a real three-tier tariff - 0.121 off-peak 00:00-05:00, 0.411 mid
 * 06:00-15:00, 0.624 on-peak 16:00-20:00, 0.411 again 21:00-23:00 - where the
 * husky-2040-prod result holds Buy at one constant all year and Sell at 0 and
 * so cannot distinguish any of this.
 *
 * The file own arithmetic settles it. "Load Cost ($)" is the hour energy priced
 * at a rate, so it says which hour rate applied. Over the 1463 rows where the
 * rate CHANGES at that stamp - the only rows that can tell the two readings
 * apart - Load Cost matched "Load (kWh)" * the SAME row Buy in 1463 of 1463,
 * and the PREVIOUS row Buy in 0 of 1463:
 *
 *     01/01/2040 06:00   load 6.0000   prev 0.121   this 0.411
 *                        cost 2.47  =  6.0000 * 0.411, not * 0.121
 *
 * So the rate stamped t priced the energy of [t, t+1), and the energy of
 * [t, t+1) is the row stamped t. The rate stamped t is the rate in force from
 * t, the same convention the energy columns use, and "end" is right for both.
 *
 * -- Why "end" is the step mode that says that ----------------------------
 * Measured out of ECharts 6 rather than taken from the name (the modes are
 * named after where the vertical goes, which is easy to read backwards):
 *
 *     step: "end"     value at x_i runs flat from x_i to x_(i+1), then jumps
 *     step: "start"   value at x_i runs flat from x_(i-1) to x_i
 *     step: "middle"  value at x_i is centred on x_i, jumping at half-hours
 *
 * The axis is a category axis labelled with those same stamps, so the screen
 * span between the "07:00" tick and the "08:00" tick IS the hour 07:00-08:00.
 * "end" puts the 07:00 bucket plateau exactly there and jumps on the boundary.
 * "start" would draw it over 06:00-07:00 - every value an hour early. "middle"
 * would jump at 07:30, splitting each bucket across two hours.
 *
 * -- Why step at all ------------------------------------------------------
 * For the series that qualify, a smoothed or straight line asserts the quantity
 * moved continuously between two figures when the dispatch is piecewise
 * constant by construction. The curve was also actively misleading where such a
 * series touches zero: utils/twinStrokeSeries.js used to write a 0 into BOTH
 * strokes at every plug/driving hand-over, and smooth fitted a curve THROUGH
 * those zeros, rounding the hand-over into a swoop and making the two halves
 * look as though they overlapped and wobbled where the values were flat.
 *
 * Stepping those series then exposed the other half of that join. A zero has no
 * width under a curve, but under step it owns a whole interval, so the zero
 * written at a DRIVING index drew a solid segment along the axis across an hour
 * the vehicle was away - reported as "an extra line that is solid under the
 * dashed line". The join is gone as a result: under step each stroke reaches
 * the hand-over boundary on its own and nothing has to be inserted. See the
 * join note in utils/twinStrokeSeries.js for the one case that still needs
 * one.
 *
 * step and smooth are contradictory and must never both be set - the ECharts
 * behaviour when they are is not documented and not worth relying on.
 *
 * The name is historical - hourly rows were the only case when this was
 * introduced. Before reusing it on a coarser aggregation, check both halves
 * again: that the bucket stamp labels its START, and that the aggregate is a
 * constant rather than a mean of something continuous.
 *
 * One coarser bucket has passed that check and uses this: the MONTHLY demand
 * charge in DemandChargeDashboard/DemandChargePage.jsx. A demand charge is
 * billed once on the month's peak rather than accrued through the month, and
 * the file's own identity - charge[M] == rate * maxPeak[M], holding for 12 of
 * 12 months and 0 of 11 on either neighbour - fixes the stamp to its own month.
 * The derivation is recorded at that series. Note what it is NOT: the daily
 * min/max/mean aggregates in TransferDailyRangeChart look like the same shape
 * and fail the first half of the test, because they average a continuously
 * varying quantity instead of reporting a figure that was constant.
 */
export const HOURLY_BUCKET_STEP = "end";

/**
 * X-axis label for one result timestamp: "MM/DD/YYYY HH:mm".
 *
 * Byte-identical in ConsumptionChart, ProductionChart, StorageChart,
 * OverlayChart, PricingChart and TransferChart before this module.
 *
 * Note this reads the stamp through `new Date()` and local getters, so it is
 * only safe for AXIS LABELS. Period keys — which day/month/year a row belongs
 * to — must come from utils/simulationDate.js instead: `new Date()` shifts an
 * ISO date-only or explicit-Z stamp by a day in a negative-offset timezone, and
 * a label that is off by a day is cosmetic where a period key that is off by a
 * day silently moves a row into the wrong bucket.
 *
 * @param {string} dateStr
 * @returns {string} the formatted label, or `dateStr` unchanged if unparseable.
 */
export const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  const MM = String(date.getMonth() + 1).padStart(2, "0");
  const DD = String(date.getDate()).padStart(2, "0");
  const YYYY = date.getFullYear();
  const HH = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${MM}/${DD}/${YYYY} ${HH}:${mm}`;
};

/**
 * X-axis name for a period view. Identical in all six charts before this
 * module.
 *
 * @param {"daily"|"monthly"|"yearly"|"all time view"} view
 * @returns {string}
 */
export const getXAxisTitle = (view) => {
  switch (view) {
    case "yearly":
      return "Months";
    case "monthly":
      return "Days";
    case "daily":
      return "Hours";
    case "all time view":
      return "Years";
    default:
      return "Date/Time";
  }
};

/**
 * The dark tooltip skin five of the six charts set by hand.
 *
 * ECharts' tooltip defaults are a white background with a 1px border, so both
 * the background and the text colour have to be stated for a dark box; the two
 * arguments below are the only things the five copies disagreed about.
 *
 * @param {{fontSize?: number, borderColor?: string, formatter?: Function,
 *          trigger?: string}} [options]
 * @returns {object} a tooltip option fragment.
 */
export const darkTooltip = ({
  fontSize,
  borderColor,
  formatter,
  trigger = "axis",
} = {}) => {
  const textStyle = { color: "white" };
  if (fontSize != null) textStyle.fontSize = fontSize;

  const tooltip = {
    trigger,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    textStyle,
  };
  /* Absent, not undefined: ECharts reads borderColor off the option object, and
   * the two charts that never set it must keep the default border. */
  if (borderColor != null) tooltip.borderColor = borderColor;
  if (formatter) tooltip.formatter = formatter;
  return tooltip;
};

/**
 * The `[slider, inside]` dataZoom pair.
 *
 * Every chart carries the same two entries; what differs is the accent colour
 * of the slider handles (green Storage, orange Production, red Consumption,
 * purple Transfer and TransferDailyRange), the translucent filler over the
 * slider's preview chart, and where the slider is parked. Pricing and Overlay
 * style neither, so passing no `accent` emits the bare slider they had.
 *
 * @param {object} [options]
 * @param {string} [options.accent] - handle / selection colour. Omit for an
 *   unstyled slider (Pricing, Overlay).
 * @param {string} [options.fillerColor] - translucent selection fill. Must stay
 *   translucent: it sits over the slider's own preview chart, so an opaque
 *   colour hides the very thing the slider exists to show.
 * @param {boolean} [options.chrome=true] - the transparent border and #f5f5f5
 *   track six of the seven sliders set. OverlayChart sets neither.
 * @param {number} [options.bottom=40]
 * @param {number} [options.height] - slider thickness; omit for the default.
 * @param {number} [options.start=0]
 * @param {number} [options.end=100]
 * @param {boolean} [options.insideGestures=true] - false emits a bare `inside`
 *   entry with no gesture flags, which is what OverlayChart had.
 * @returns {object[]}
 */
export const buildDataZoom = ({
  accent,
  fillerColor,
  chrome = true,
  bottom = 40,
  height,
  start = 0,
  end = 100,
  insideGestures = true,
} = {}) => {
  const slider = { type: "slider", show: true, start, end };
  if (height != null) slider.height = height;
  slider.bottom = bottom;

  if (chrome) {
    slider.borderColor = "transparent";
    slider.backgroundColor = "#f5f5f5";
  }
  if (fillerColor) slider.fillerColor = fillerColor;
  if (accent) {
    slider.handleStyle = { color: accent, borderColor: accent };
    slider.moveHandleStyle = { color: accent };
    slider.selectedDataBackground = {
      lineStyle: { color: accent },
      areaStyle: { color: accent },
    };
  }

  const inside = { type: "inside", start, end };
  if (insideGestures) {
    inside.zoomOnMouseWheel = true;
    inside.moveOnMouseMove = true;
  }

  return [slider, inside];
};
